<script setup>
import {computed, ref, watch} from 'vue'
import {getApiBaseUrl, showSuccessMessage} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue'
import {createConfirmDialog} from "vuejs-confirm-dialog";
import IntermediaryService from "@/services/IntermediaryService";
import router from "@/router";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import {ErrorMessage, Field, Form} from 'vee-validate';
import {useI18n} from 'vue-i18n';
import {authData} from '@/services/AuthService';

const {t} = useI18n();

const props = defineProps({
  intermediary: {type: Object, required: false},
});

// Smazání celého zprostředkovatele je nevratné a smaže i navázanou strukturu zástupců/klientů -
// tlačítko proto smí vidět jen SUPERADMIN, i backend to nezávisle vynucuje (IntermediaryController::delete()).
const canDeleteIntermediary = computed(() => authData?.loggedUser?.role === 'SUPERADMIN');

const defaultTheme = ref({primaryColor: '', primaryTextColor: '',primaryHoverColor: '', primaryHoverTextColor: '', primaryInterfaceColor: '', primaryInterfaceTextColor: ''})
const intermediary = ref({intermediary_name: '', abbr: '', email: '', email_back_office: '', phone: '', tax_number: '', bank_account_number: '', iban: '', password: '', pasword_conf: '', address: {street: '', city: '', zip_code: '', country: ''}, theme: {...defaultTheme.value}});

const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);

const passwordMismatch = ref(false);

watch([() => intermediary.value.password, () => intermediary.value.password_conf], () => {
  passwordMismatch.value = intermediary.value.password && intermediary.value.password_conf && intermediary.value.password !== intermediary.value.password_conf;
});

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', intermediary.value);
      isOpen.value = false;
    } else {
      emit('cancel');
      isOpen.value = false;
    }
  }, 300);
}

if (props.intermediary) {
  // Deep clone to prevent modal mutations from propagating to the parent view before save.
  intermediary.value = JSON.parse(JSON.stringify(props.intermediary));
  if (!intermediary.value.address) {
    intermediary.value.address = {street: '', city: '', zip_code: '', country: ''};
  }
}

function deleteIntermediary() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('createUpdateIntermediaryModal.delete_intermediary'),
    message: t('createUpdateIntermediaryModal.delete_intermediary_confirm'),
  });
  onConfirm(() => {
    IntermediaryService.deleteIntermediary(intermediary.value.id).then(() => {
      showSuccessMessage(t('createUpdateIntermediaryModal.intermediary_deleted'));
      router.push({name: 'Intermediaries'});
    });
  });
  reveal();
}

function openModal() {
  isOpen.value = true
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle v-if="intermediary.id" as="h3" class="modal-title">
                {{ t('createUpdateIntermediaryModal.intermediary_edit') }}
              </DialogTitle>
              <DialogTitle v-else as="h3" class="modal-title">
                {{ t('createUpdateIntermediaryModal.intermediary_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <Form ref="form" v-on:submit="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="intermediaryNameInput" class="form-label">{{ t('createUpdateIntermediaryModal.intermediary_name') }}</label>
                    <Field v-model="intermediary.intermediary_name" name="intermediaryNameInput" id="intermediaryNameInput" class="form-control" rules="required"/>
                    <ErrorMessage name="intermediaryNameInput"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="abbrInput" class="form-label">{{ t('createUpdateIntermediaryModal.abbr') }}</label>
                    <Field v-model="intermediary.abbr" name="abbrInput" id="abbrInput" class="form-control" rules="required"/>
                    <ErrorMessage name="abbrInput"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="codeInput" class="form-label">{{ t('createUpdateIntermediaryModal.code') }}</label>
                    <Field v-model="intermediary.code" name="codeInput" id="codeInput" class="form-control" type="text"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 col-md-6 form-group">
                    <label for="businessNumberInput" class="form-label">{{ t('createUpdateIntermediaryModal.ico') }}</label>
                    <Field v-model="intermediary.business_number" name="businessNumberInput" id="businessNumberInput" class="form-control" type="text"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="taxNumberInput" class="form-label">{{ t('createUpdateIntermediaryModal.dic') }}</label>
                    <Field v-model="intermediary.tax_number" name="taxNumberInput" id="taxNumberInput" class="form-control" type="text"/>
                  </div>
                </div>
                <fieldset class="mb-3">
                  <legend>{{ t('createUpdateIntermediaryModal.company_address') }}</legend>
                  <div class="row mb-2">
                    <div class="col-12 form-group">
                      <label for="addressStreetInput" class="form-label">{{ t('createUpdateIntermediaryModal.address_street') }}</label>
                      <Field v-model="intermediary.address.street" name="addressStreetInput" id="addressStreetInput" class="form-control" type="text"/>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-12 col-md-6 form-group">
                      <label for="addressCityInput" class="form-label">{{ t('createUpdateIntermediaryModal.address_city') }}</label>
                      <Field v-model="intermediary.address.city" name="addressCityInput" id="addressCityInput" class="form-control" type="text"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="addressZipCodeInput" class="form-label">{{ t('createUpdateIntermediaryModal.address_zip_code') }}</label>
                      <Field v-model="intermediary.address.zip_code" name="addressZipCodeInput" id="addressZipCodeInput" class="form-control" type="text"/>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 form-group">
                      <label for="addressCountryInput" class="form-label">{{ t('createUpdateIntermediaryModal.address_country') }}</label>
                      <Field v-model="intermediary.address.country" name="addressCountryInput" id="addressCountryInput" class="form-control" type="text"/>
                    </div>
                  </div>
                </fieldset>
                <div class="row mb-3">
                  <div class="col-12 col-md-6 form-group">
                    <label for="accountNumberInput" class="form-label">{{ t('createUpdateIntermediaryModal.account_number') }}</label>
                    <Field v-model="intermediary.bank_account_number" name="accountNumberInput" id="accountNumberInput" class="form-control" type="text"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="ibanInput" class="form-label">{{ t('createUpdateIntermediaryModal.iban') }}</label>
                    <Field v-model="intermediary.iban" name="ibanInput" id="ibanInput" class="form-control" type="text"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="emailBackOfficeInput" class="form-label">{{ t('createUpdateIntermediaryModal.email_back_office') }}</label>
                    <Field v-model="intermediary.email_back_office" name="emailBackOfficeInput" id="emailBackOfficeInput" class="form-control" rules="email" type="email"/>
                    <ErrorMessage name="emailBackOfficeInput"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="emailInput" class="form-label">{{ t('createUpdateIntermediaryModal.email') }}</label>
                    <Field v-model="intermediary.email" name="emailInput" id="emailInput" class="form-control" rules="email" type="email"/>
                    <ErrorMessage name="emailInput"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="phoneNumber" class="form-label">{{ t('createUpdateIntermediaryModal.phone_number') }}</label>
                    <Field v-model="intermediary.phone" name="phoneNumber" id="phoneNumber" rules="phoneNumber" class="form-control" type="text"/>
                    <ErrorMessage name="phoneNumber"/>
                  </div>
                </div>
                <fieldset>
                  <legend>{{ t('createUpdateIntermediaryModal.theme') }}</legend>
                  <div class="row">
                    <div class="col-12 col-md-6 form-group">
                      <label for="themePrimaryColor" class="form-label">{{ t('createUpdateIntermediaryModal.primary_color') }}</label>
                      <Field v-model="intermediary.theme.primaryColor" name="themePrimaryColor" id="themePrimaryColor" class="form-control" rules="required" type="color"/>
                      <ErrorMessage name="themePrimaryColor"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="themePrimaryTextColor" class="form-label">{{ t('createUpdateIntermediaryModal.primary_color_text') }}</label>
                      <Field v-model="intermediary.theme.primaryTextColor" name="themePrimaryTextColor" id="themePrimaryTextColor" rules="required" class="form-control" type="color"/>
                      <ErrorMessage name="themePrimaryTextColor"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="themePrimaryHoverColor" class="form-label">{{ t('createUpdateIntermediaryModal.primary_color_hover') }}</label>
                      <Field v-model="intermediary.theme.primaryHoverColor" name="themePrimaryHoverColor" id="themePrimaryHoverColor" class="form-control" rules="required" type="color"/>
                      <ErrorMessage name="themePrimaryHoverColor"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="themePrimaryHoverTextColor" class="form-label">{{ t('createUpdateIntermediaryModal.primary_color_text_hover') }}</label>
                      <Field v-model="intermediary.theme.primaryHoverTextColor" name="themePrimaryHoverTextColor" id="themePrimaryHoverTextColor" rules="required" class="form-control" type="color"/>
                      <ErrorMessage name="themePrimaryHoverTextColor"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="themePrimaryInterfaceColor" class="form-label">{{ t('createUpdateIntermediaryModal.primary_color_environment') }}</label>
                      <Field v-model="intermediary.theme.primaryInterfaceColor" name="themePrimaryInterfaceColor" id="themePrimaryInterfaceColor" class="form-control" rules="required" type="color"/>
                      <ErrorMessage name="themePrimaryInterfaceColor"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="themePrimaryInterfaceTextColor" class="form-label">{{ t('createUpdateIntermediaryModal.primary_color_text_environment') }}</label>
                      <Field v-model="intermediary.theme.primaryInterfaceTextColor" name="themePrimaryInterfaceTextColor" id="themePrimaryInterfaceTextColor" rules="required" class="form-control" type="color"/>
                      <ErrorMessage name="themePrimaryInterfaceTextColor"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="themeSecondaryInterfaceColor" class="form-label">{{ t('createUpdateIntermediaryModal.secondary_color_environment') }}</label>
                      <Field v-model="intermediary.theme.secondaryInterfaceColor" name="themeSecondaryInterfaceColor" id="themeSecondaryInterfaceColor" class="form-control" rules="required" type="color"/>
                      <ErrorMessage name="themeSecondaryInterfaceColor"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="themeSecondaryInterfaceTextColor" class="form-label">{{ t('createUpdateIntermediaryModal.secondary_color_text_environment') }}</label>
                      <Field v-model="intermediary.theme.secondaryInterfaceTextColor" name="themeSecondaryInterfaceTextColor" id="themeSecondaryInterfaceTextColor" rules="required" class="form-control" type="color"/>
                      <ErrorMessage name="themeSecondaryInterfaceTextColor"/>
                    </div>
                  </div>
                </fieldset>
                <!--                <div v-if="!intermediary?.id">
                                  <div class="row mb-3">
                                    <div class="col-12 form-group">
                                      <label for="passwordInput" class="form-label">Heslo</label>
                                      <input v-model="intermediary.password" id="passwordInput" class="form-control" type="password"/>
                                    </div>
                                  </div>
                                  <div class="row mb-3">
                                    <div class="col-12 form-group">
                                      <label for="passwordConfirmationInput" class="form-label">Potvrzení hesla</label>
                                      <input v-model="intermediary.password_conf" id="passwordConfirmationInput" class="form-control" type="password"/>
                                      <span v-if="passwordMismatch" class="text-danger">Hesla se neshodují</span>
                                    </div>
                                  </div>
                                </div>-->
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateIntermediaryModal.cancel') }}</button>
                <button v-if="intermediary.id && canDeleteIntermediary" class="btn btn-danger btn-md" @click.prevent="deleteIntermediary">{{ t('createUpdateIntermediaryModal.delete') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('createUpdateIntermediaryModal.save') }}</button>
              </div>
            </Form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>



